import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/Header.css';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginForm from './LoginForm';
import { stopSSE } from './realtime';

// let LOGIN_ROUTES = ['/', '/forgot-password', '/reset-password'];

/** Returns up to 2 uppercase initials from a display name */
function getInitials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function Header() {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [company, setCompany] = useState({ name: '', logo: '' });

  const navigate = useNavigate();
  const location = useLocation();

  const loggedInUser = sessionStorage.getItem('name') ?? '';
  const productionName = sessionStorage.getItem('isAdmin') !== 'true'
    ? sessionStorage.getItem('group_id')
    : null;

  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';

  const LOGIN_ROUTES = isLoggedIn
    ? ['/', '/forgot-password', '/reset-password']
    : ['/', '/forgot-password', '/reset-password', '/support-center'];

  const isLoginPage = LOGIN_ROUTES.includes(location.pathname);

  const fetchDetails = () => {
    fetch('/company.json')
      .then((res) => res.json())
      .then((data) => {
        setCompany({ name: data.name, logo: data.logo });
        sessionStorage.setItem('companyName', data.name);
        sessionStorage.setItem('companyLogo', data.logo);
        sessionStorage.setItem('baseURL', data.baseURL);
      })
      .catch((err) => console.error('Error fetching company data:', err));
  };

  useEffect(() => {
    fetchDetails()
  }, []);

  const confirmLogout = () => {
    setShowLogoutModal(false);
    stopSSE();
    sessionStorage.clear();
    fetchDetails();
    navigate('/');
  };

  return (
    <>
      {/* ════════════════════════════════
          HEADER
      ════════════════════════════════ */}
      <header className="header">

        {/* Left navy-to-purple accent bar */}
        <div className="header__accent" aria-hidden="true" />

        {/* Logo */}
        <div className="header__logo">
          {company.logo && (
            <img
              src={company.logo}
              alt={`${company.name} logo`}
              className="header__logo-img"
            />
          )}
          <img
            src="QRmor.jpeg"
            alt={`QRmorlogo`}
            className="header__logo-img"
            style={{
              borderLeft: '1px solid var(--c-navy-dark)',
              padding: '0 0px',
            }}
          />
        </div>

        {/* Auth section */}
        <div className="header__auth">
          {!isLoginPage && (
            /* ── Authenticated: user chip + logout ── */
            <>
              <div className="header__user-chip">
                <div className="header__user-avatar" aria-hidden="true">
                  {getInitials(loggedInUser)}
                </div>
                <div className="header__user-info">
                  <span className="header__user-label">Signed in as</span>
                  <span className="header__user-name">{loggedInUser}</span>
                  {productionName && (
                    <span className="header__user-label">{productionName}</span>
                  )}
                </div>
              </div>
              <div className="header__divider" aria-hidden="true" />
              <div className='header_logoutSection'>
                <button
                  className="header__btn header__btn--logout"
                  onClick={() => setShowLogoutModal(true)}
                >
                  Logout
                </button>
                <span className="header__support" onClick={() => { navigate('/support-center') }}>Contact Support Center</span>
              </div>
            </>
          )}
        </div>
      </header>

      {/* ════════════════════════════════
          LOGOUT CONFIRM MODAL
      ════════════════════════════════ */}
      {showLogoutModal && createPortal(
        <div
          className="modal-overlay"
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            className="modal logout"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-title"
          >
            {/* Navy band */}
            <div className="modal__band">
              <div className="modal__band-icon" aria-hidden="true">🔒</div>
              <span className="modal__band-title" id="logout-title">
                Confirm Sign Out
              </span>
            </div>

            {/* Body */}
            <div className="modal__body">
              <p>Are you sure you want to log out of your current session?</p>
              <div className="modal__actions">
                <button
                  className="modal__btn modal__btn--cancel"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal__btn modal__btn--confirm"
                  onClick={confirmLogout}
                >
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ════════════════════════════════
          LOGIN MODAL
      ════════════════════════════════ */}
      {showLoginModal && createPortal(
        <div
          className="modal-overlay"
          onClick={() => setShowLoginModal(false)}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Login"
          >
            {/* Navy band with × close */}
            <div className="modal__band">
              <div className="modal__band-icon" aria-hidden="true">👤</div>
              <span className="modal__band-title">Sign In</span>
            </div>

            {/* × button sits inside the band */}
            <button
              className="modal__close"
              onClick={() => setShowLoginModal(false)}
              aria-label="Close login modal"
            >
              &times;
            </button>

            {/* LoginForm renders its own padded body */}
            <LoginForm closeLoginModal={() => setShowLoginModal(false)} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default Header;